import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Heart, Shield, Users, Lightbulb, ExternalLink, Phone, Mail } from 'lucide-react';

export default function About() {
  const navigate = useNavigate();

  const values = [
    {
      icon: Heart,
      title: 'Compassion First',
      description: 'We understand the complexity of living with an invisible disability. Our guidance is always delivered with empathy.'
    },
    {
      icon: Shield,
      title: 'Your Privacy Matters',
      description: 'Anonymous mode, no data selling, and you control what gets saved. Your journey is yours alone.'
    },
    {
      icon: Users,
      title: 'Community-Driven',
      description: 'Built with input from the disability community, ensuring real-world relevance and sensitivity.'
    },
    {
      icon: Lightbulb,
      title: 'Practical Support',
      description: 'Not just advice, but actual scripts you can use. We bridge the gap between knowing and doing.'
    }
  ];

  return (
    <div className="min-h-screen pb-safe">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>

        <div className="mt-8 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">
            About PwSD Navigator
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            Navigating life with an invisible disability often means facing difficult decisions about when, how, and whether to disclose your condition.
          </p>
        </div>

        {/* Mission */}
        <div className="mt-8 card-soft bg-secondary/50 page-enter stagger-1">
          <h2 className="font-heading text-xl font-semibold text-foreground">
            Our Mission
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            To empower individuals with silent (invisible and dynamic) disabilities by providing resources, fostering understanding, and ensuring no one suffers in silence—promoting a life of endless possibilities through education, support, and community engagement.
          </p>
        </div>

        {/* Vision */}
        <div className="mt-4 card-soft page-enter stagger-1">
          <h2 className="font-heading text-xl font-semibold text-foreground">
            Our Vision
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            A world where individuals with silent disabilities are empowered to lead a life free from suffering in silence, thriving out loud with endless possibilities.
          </p>
        </div>

        {/* Understanding Silent Disabilities */}
        <div className="mt-8 page-enter stagger-2">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
            Understanding Silent Disabilities
          </h2>
          <div className="card-soft">
            <p className="text-muted-foreground leading-relaxed">
              Silent or invisible disabilities encompass a range of conditions, including cognitive disorders, autoimmune diseases, and mental health issues. Although these disabilities may not be physically apparent, they can profoundly affect daily life, communication, and participation in activities.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Dynamic disabilities</strong> are characterized by their fluctuating nature, with symptoms that can worsen or improve unpredictably. This variability presents unique challenges that necessitate continuous adaptation and advocacy.
            </p>
          </div>
        </div>

        {/* Why Awareness Matters */}
        <div className="mt-6 card-soft bg-primary/5 border-primary/20 page-enter stagger-2">
          <h3 className="font-heading font-semibold text-foreground mb-3">
            Why Awareness Matters
          </h3>
          <ul className="space-y-3 text-muted-foreground">
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">•</span>
              <span><strong className="text-foreground">Build Empathy</strong> – Support those who may be suffering in silence</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">•</span>
              <span><strong className="text-foreground">Improve Accessibility</strong> – Ensure accommodations for fluctuating needs</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-primary font-bold">•</span>
              <span><strong className="text-foreground">Expand Resources</strong> – Advocate for better policies and community support</span>
            </li>
          </ul>
        </div>

        {/* Values */}
        <div className="mt-8 space-y-4">
          <h2 className="font-heading text-xl font-semibold text-foreground page-enter stagger-2">
            Our Values
          </h2>
          
          {values.map((value, index) => (
            <div key={value.title} className={`card-soft page-enter stagger-${index + 2}`}>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-secondary rounded-2xl">
                  <value.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground">
                    {value.title}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {value.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How To Use */}
        <div className="mt-10 page-enter stagger-4">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
            How To Use PwSD Navigator
          </h2>
          
          <div className="space-y-6">
            {/* Track Your Patterns */}
            <div className="card-soft">
              <h3 className="font-heading font-semibold text-foreground mb-2">Track Your Patterns</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Use the Symptom Tracker to record daily experiences:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Log pain, energy, sleep, and mood levels (1-10)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Mark body parts affected and track triggers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Record functional impact on mobility, work, and daily activities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Mark flare days or symptom-free days</span>
                </li>
              </ul>
            </div>

            {/* Prepare for Appointments */}
            <div className="card-soft">
              <h3 className="font-heading font-semibold text-foreground mb-2">Prepare for Appointments</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Turn your tracked data into professional healthcare reports:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Go to Healthcare Export from the Symptom Tracker</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Select a date range (7 days to 1 year)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Download a PDF with severity charts, frequency data, and trends</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Print or share with your healthcare provider</span>
                </li>
              </ul>
            </div>

            {/* Practice Conversations */}
            <div className="card-soft">
              <h3 className="font-heading font-semibold text-foreground mb-2">Practice Conversations</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Build confidence with AI-guided coaching:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Choose a scenario: workplace, medical, school, travel, or social</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Answer a few questions about your situation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Get a risk assessment, recommended action, and ready-to-use scripts</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Save favorite scripts for quick access later</span>
                </li>
              </ul>
            </div>

            {/* Reflect and Develop */}
            <div className="card-soft">
              <h3 className="font-heading font-semibold text-foreground mb-2">Reflect and Develop Skills</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Use journaling and guided activities:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Write journal entries with mood tags to track emotional patterns</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Explore the Skills Hub for guided and practice exercises</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Build skills in emotional regulation, social situations, and sensory management</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>View Weekly Insights for AI analysis of your patterns</span>
                </li>
              </ul>
            </div>

            {/* Find Words That Work */}
            <div className="card-soft">
              <h3 className="font-heading font-semibold text-foreground mb-2">Find Words That Work</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Access disclosure tools when you're ready to share:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Browse the Conditions Guide for disclosure tips on 23+ conditions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Print disclosure cards to hand to others when words are hard</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Share cards via link with coworkers, doctors, or family</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Use Panic Mode for quick scripts when you need words fast</span>
                </li>
              </ul>
            </div>

            {/* On Difficult Days */}
            <div className="card-soft bg-amber-50 border-amber-200">
              <h3 className="font-heading font-semibold text-foreground mb-2">On Difficult Days</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Low energy? Use Flare Day Mode:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  <span>Tap "Having a flare day?" on the Dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  <span>Log your energy with one tap</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  <span>Access pre-written scripts for canceling plans or asking for rest</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600">•</span>
                  <span>Everything is simplified for low cognitive load</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* PwSD Association */}
        <div className="mt-10 card-soft border-primary/30 page-enter stagger-4">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-3">
            Persons with Silent Disabilities Association
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            PwSD Association is a 501(c)(3) nonprofit committed to empowering, supporting and advocating for individuals with invisible and dynamic disabilities. They provide resources, foster community support, and promote acceptance.
          </p>
          <div className="mt-4 space-y-2">
            <a 
              href="https://silent-disabilities.org" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              <span>silent-disabilities.org</span>
            </a>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4" />
              <span>24/7 Info Line: 667.244.3966</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>info@silent-disabilities.org</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 space-y-3 page-enter stagger-4">
          <Button
            data-testid="get-started-btn"
            onClick={() => navigate('/auth')}
            className="btn-primary w-full"
          >
            Get Started Free
          </Button>
          <Button
            data-testid="view-resources-btn"
            variant="outline"
            onClick={() => navigate('/resources')}
            className="w-full"
          >
            View Resources
          </Button>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center pb-8">
          <p className="text-sm text-muted-foreground">
            Made with care for the disability community 💚
          </p>
        </div>
      </div>
    </div>
  );
}
